import { useCallback } from 'react'
import FloatingMenu from 'Shared/components/FloatingMenu/FloatingMenu'
import useAct from 'FlowOverview/useAct'
import restClient from 'Shared/hooks/restClient'
import SimplePaginator from 'Shared/components/SimplePaginator'
import BackdropModal from 'Shared/components/BackdropModal/BackdropModal'
import { useWindowSize } from 'usehooks-ts'
import { MOBILE_SCREEN_WIDTH } from 'Shared/constants'
import useScriptActions from 'Shared/ScriptEditor/hooks/useAct'
import { ManageMenuItem } from 'Shared/components/ManageMenu';
import ManageMenuComponent from 'Shared/components/ManageMenu/ManageMenuComponent';
import CopyToClipboard from 'Shared/components/CopyToClipboard'
import EditableName from 'Shared/components/EditableName/EditableName'
import FlowHeader from './FlowHeader'
import FlowSteps from './FlowSteps'
import TrafficSourcesTable from './TrafficSourcesTable'
import './PageContainer.scoped.scss'
import MovementSourcesTable from './MovementSourcesTable'
import RefreshTargets from 'Shared/components/RefreshTargets'
import { ConfirmationModal } from 'Shared/components/ConfirmationModal/ConfirmationModal'

const items = [
  { name: 'Status', tag: 'status' },
  { name: 'Sources', tag: 'sources' },
  { name: 'Analytics', tag: 'analytics' }
]

export default function PageContainer({ flowId, flowName, surveyId }) {
  const act = useAct()
  const scriptActions = useScriptActions()
  const { status, publicUrl, section } = useSel((s) => s.flow)
  const participationMode = useSel(s => s.flow.actions?.find(a => a.kind === 'survey')?.survey?.participationMode || 'open')
  const flowTemplate = useSel((s) => s.flow.flowTemplate)
  const sourceAttribution = useSel((s) => s.flow.stats?.sourceAttribution)
  const movementSourceAttribution = useSel((s) => s.flow.stats?.movementSourceAttribution)
  const [overviewPage, setOverviewPage] = useState(1)
  const [duplicateFlowModalVisible, setDuplicateFlowModalVisible] = useState(false)
  const [duplicateFlowName, setDuplicateFlowName] = useState('')
  const [duplicateModalError, setDuplicateModalError] = useState(null)
  const [refreshTargetsModalOpen, setRefreshTargetsModalOpen] = useState(false)
  const [archiveConfirmModalOpen, setArchiveConfirmModalOpen] = useState(false)
  const size = useWindowSize()

  useEffect(() => {
    if (flowId) {
      act.flow.load(flowId)
      act.flow.loadStats(flowId)
    }
  }, [flowId])

  useEffect(() => {
    if (surveyId) {
      scriptActions.scriptBuilder.load(surveyId)
      const questionInterval = setInterval(() => {
        scriptActions.scriptBuilder.load(surveyId)
      }, 5000)
      return () => {
        clearInterval(questionInterval)
      }
    }
  }, [surveyId])

  const handleArchiveFlow = useCallback(() => {
    restClient.delete(`/api/flows/${flowId}/`)
      .then(() => {
        window.location.href = `/admin/${section === 'ballots' ? 'ballots' : 'flows'}`
      })
  }, [flowId, section])

  const menuItems: ManageMenuItem[] = [
    {
      title: <span className="flex items-center">Edit</span>,
      onClick: () => window.location.href = `/admin/flows/${flowId}/build`
    },
    {
      title: <span className="flex items-center">Duplicate page</span>,
      onClick: () => setDuplicateFlowModalVisible(true)
    }
  ]
  if (participationMode === 'closed') {
    menuItems.push({
      title: <span className="flex items-center">Refresh audience</span>,
      onClick: () => setRefreshTargetsModalOpen(true)
    })
  }
  if (status === 'closed') {
    menuItems.push({
      title: <span className="flex items-center">Archive</span>,
      onClick: () => setArchiveConfirmModalOpen(true)
    })
  }

  const handleCloneFlow = useCallback(() => {
    setDuplicateModalError(null)
    if (!duplicateFlowName || duplicateFlowName.trim().length === 0) {
      return
    }
    restClient.post(`/api/flows/${flowId}/clone`, { name: duplicateFlowName })
      .then(({ data }) => {
        window.location.href = `/admin/flows/${data.id}/build`
      }).catch((error) => {
        setDuplicateModalError(error.response?.data?.error || 'An error occurred while duplicating the dashboard.')
      })
  }, [duplicateFlowName, flowId])

  const refreshTargetPopup = useMemo(() => {
    if (!refreshTargetsModalOpen) { return null; }

    return <RefreshTargets 
      onComplete={() => setRefreshTargetsModalOpen(false)} 
      onClose={() => setRefreshTargetsModalOpen(false)} 
      entityName={section === 'ballots' ? 'ballot' : 'survey'}
      refreshTargetFunction={(dryRun) => act.flow.refreshTargets({ dryRun })}
      onCommit={() => act.flow.loadStats(flowId)}
      />
  }, [refreshTargetsModalOpen, flowId, section]);

  return (
    <>
      <div className="header editable-name-container flow">
        <a className='back' href={`/admin/${section === 'ballots' ? 'ballots' : 'flows'}`}></a>
        <EditableName
          modelName="flow"
          endpoint={`/api/flows/${flowId}`}
          status={flowTemplate?.name}
          noMarginBottom={true}
        />
        <div className="actions">
          <ManageMenuComponent
            items={menuItems}
            components={[]}
            buttonType='secondary'
          />
          <a className='button primary' target='_blank' href={`${publicUrl}?forget=1&checkAdmin=1`} rel="noreferrer">View</a>
        </div>
      </div>
      <div className='url'>
        <span>{publicUrl}</span>
        <div className='copier'><CopyToClipboard text="Copy" url={publicUrl} /></div>
      </div>

      <div className='flow-overview margin-top double'>
        <div className="left">
          {size.width >= MOBILE_SCREEN_WIDTH && <FloatingMenu items={items} />}
          {size.width < MOBILE_SCREEN_WIDTH && (
            <SimplePaginator
              totalPages={3}
              currentPage={overviewPage}
              onChange={setOverviewPage}
              labels={items.map((item) => item.name)}
              classes="margin-bottom"
            />
          )}
        </div>
        <div className='right'>
          {(size.width >= MOBILE_SCREEN_WIDTH || overviewPage === 1) && (
            <FlowHeader />
          )}
          {(size.width >= MOBILE_SCREEN_WIDTH || overviewPage === 2) && (<>
            <MovementSourcesTable movementSourceAttribution={movementSourceAttribution} />
            <TrafficSourcesTable sourceAttribution={sourceAttribution} />
          </>)}
          {(size.width >= MOBILE_SCREEN_WIDTH || overviewPage === 3) && (
            <FlowSteps />
          )}
        </div>
      </div>

      <BackdropModal
        title="Duplicate Page"
        open={duplicateFlowModalVisible}
        onCancel={() => setDuplicateFlowModalVisible(false)}
      >
        <div className='form narrow'>
          <div className='field'>
            <label>New page name</label>
            <input type='text' placeholder='Name your page' value={duplicateFlowName} onChange={(e) => setDuplicateFlowName(e.target.value)} />
          </div>
          <button
            className='button block primary no-margin-left'
            onClick={() => handleCloneFlow()}
            disabled={!duplicateFlowName || duplicateFlowName.trim().length === 0}
          >Duplicate</button>
          {duplicateModalError && <p className='error margin-top'>{duplicateModalError}</p>}
        </div>
      </BackdropModal>

      <ConfirmationModal
        open={archiveConfirmModalOpen}
        hide={() => setArchiveConfirmModalOpen(false)}
        onSubmit={handleArchiveFlow}
        confirmValue="delete"
      />

      {refreshTargetPopup}
    </>
  )
}
